import { useEffect, useState } from 'react';
import { cva } from 'class-variance-authority';
import clsx from 'clsx';
import { AnimatePresence, motion } from 'motion/react';
import { Text } from '@nula/core-ui';
import type { UIMessagePart, UIDataTypes, UITools } from 'ai';
import styles from './thinking-indicator.module.css';

// How long the completed reasoning bubble lingers before fading away. Gives
// the user a chance to register that the thought finished without leaving
// permanent clutter under the response. The shimmer keeps running during
// the linger so the bubble doesn't go visually "dead" before it exits.
const LINGER_MS = 2200;

const root = cva(styles.root);

const DOT_VARIANTS = {
  bounce: { y: [0, -4, 0] },
  still: { y: 0 },
};

const DOT_TRANSITION = (delay: number) => ({
  duration: 0.6,
  repeat: Infinity,
  ease: 'easeInOut' as const,
  delay,
});

function ThinkingDots() {
  return (
    <span className={styles.dots} aria-label="Thinking">
      {[0, 0.15, 0.3].map((delay, i) => (
        <motion.span
          key={i}
          className={styles.dot}
          animate={DOT_VARIANTS.bounce}
          transition={DOT_TRANSITION(delay)}
        />
      ))}
    </span>
  );
}

/** Narrow the wide UIMessagePart union down to ReasoningUIPart. */
type ReasoningPart = Extract<UIMessagePart<UIDataTypes, UITools>, { type: 'reasoning' }>;

interface ThinkingIndicatorProps {
  /** A Vercel SDK v6 ReasoningUIPart from useChat's messages[i].parts. */
  part: ReasoningPart;
  className?: string;
}

/**
 * Renders the "thinking" affordance for a single ReasoningUIPart.
 *
 * useChat accumulates `reasoning-start` / `reasoning-delta` / `reasoning-end`
 * chunks into a ReasoningUIPart. M11b's cortex serializer emits one
 * reasoning-{start,delta,end} triple per cortex thought, so a ReasoningUIPart
 * here roughly equals a single thought. When `part.text` is non-empty we
 * show the text (with the existing shimmer); when empty (or before the first
 * delta arrives) we show the animated dots.
 */
export function ThinkingIndicator({ part, className }: ThinkingIndicatorProps) {
  const isDone = part.state === 'done';

  // Linger briefly after the reasoning finishes, then unmount via AnimatePresence
  // so the user registers completion without permanent clutter. `visible` is
  // strictly local UI state — the part itself stays in messages[] for memory
  // continuity / a future "show thinking" disclosure.
  const [visible, setVisible] = useState(true);
  useEffect(() => {
    if (!isDone) return;
    const t = setTimeout(() => setVisible(false), LINGER_MS);
    return () => clearTimeout(t);
  }, [isDone]);

  return (
    <AnimatePresence>
      {visible && (
        <motion.div
          className={clsx(root(), className)}
          initial={{ opacity: 0, y: 6 }}
          animate={{ opacity: 1, y: 0 }}
          exit={{ opacity: 0, y: -4, height: 0, marginTop: 0, marginBottom: 0 }}
          transition={{ duration: 0.25, ease: 'easeOut' }}
        >
          {part.text
            ? (
              <span className={styles.textWrap}>
                <Text className={styles.text}>{part.text}</Text>
                {/* Keep the shimmer running for the full visible lifetime,
                    not just while state==='streaming'. Cortex flushes
                    reasoning-start/delta/end in a single tick so the
                    'streaming' window is sub-millisecond — gating the
                    shimmer on it makes the effect invisible. */}
                <span className={styles.shimmer} aria-hidden />
              </span>
            )
            : <ThinkingDots />
          }
        </motion.div>
      )}
    </AnimatePresence>
  );
}
